import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, setDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import './Settings.css';
import { DEFAULT_IGNORE_KEYWORDS } from '../../utils/codeProcessing';
import {
  getBuiltinIgnorePatternsForLanguage,
  getBuiltinSuggestionStringsForLanguage
} from '../../utils/builtinLanguageIgnorePatterns';
import SpacedRepetition from '../../components/SRS/SpacedRepetition';
import useUserLanguages from '../../hooks/useUserLanguages';
import { ReactComponent as AddIcon } from '../../Icons/add.svg';
import { ReactComponent as PatternKeywordIcon } from '../../Icons/pattern-keyword.svg';
import { ReactComponent as PatternCommentIcon } from '../../Icons/pattern-comment.svg';
import { ReactComponent as PatternMultilineIcon } from '../../Icons/pattern-multiline.svg';
import AccountSettings from './AccountSettings';
import IconRenderer, { normalizeLanguageIconKey } from '../ui/IconRenderer';

// Default supported languages from codeProcessing.js
const DEFAULT_LANGUAGES = ['JavaScript', 'Python', 'Java', 'C++', 'Ruby', 'PHP'];

// Pattern types
const PATTERN_TYPES = {
  SIMPLE: 'simple',
  COMMENT: 'comment',
  MULTILINE: 'multiline'
};

const SETTINGS_NAV = [
  { id: 'account', label: 'Account' },
  { id: 'study', label: 'Study' },
  { id: 'code-processing', label: 'Code Processing' },
  { id: 'notifications', label: 'Notifications' }
];

const SETTINGS_PLACEHOLDER_TITLES = {
  account: 'Account',
  study: 'Study',
  notifications: 'Notifications'
};

function SettingsNavIcon({ sectionId }) {
  switch (sectionId) {
    case 'account':
      return (
        <svg className="settings-page__nav-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="8" r="4" />
          <path d="M5 20c0-3.5 3.5-6 7-6s7 2.5 7 6" />
        </svg>
      );
    case 'study':
      return (
        <svg className="settings-page__nav-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      );
    case 'code-processing':
      return (
        <svg className="settings-page__nav-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M8 9l-3 3 3 3" />
          <path d="M16 9l3 3-3 3" />
        </svg>
      );
    case 'notifications':
      return (
        <svg className="settings-page__nav-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      );
    default:
      return null;
  }
}

// Default comment styles for each language
const LANGUAGE_COMMENT_STYLES = {
  'JavaScript': [
    { label: 'Single-line (//)', value: '//' },
    { label: 'Multi-line (/* */)', openValue: '/*', closeValue: '*/' }
  ],
  'Python': [
    { label: 'Single-line (#)', value: '#' },
    { label: 'Docstring (\'\'\')', openValue: "'''", closeValue: "'''" }
  ],
  'Java': [
    { label: 'Single-line (//)', value: '//' },
    { label: 'Multi-line (/* */)', openValue: '/*', closeValue: '*/' },
    { label: 'JavaDoc (/**)', openValue: '/**', closeValue: '*/' }
  ],
  'C++': [
    { label: 'Single-line (//)', value: '//' },
    { label: 'Multi-line (/* */)', openValue: '/*', closeValue: '*/' }
  ],
  'Ruby': [
    { label: 'Single-line (#)', value: '#' },
    { label: 'Multi-line (=begin/=end)', openValue: '=begin', closeValue: '=end' }
  ],
  'PHP': [
    { label: 'Single-line (//)', value: '//' },
    { label: 'Single-line (#)', value: '#' },
    { label: 'Multi-line (/* */)', openValue: '/*', closeValue: '*/' }
  ],
  'C': [
    { label: 'Single-line (//)', value: '//' },
    { label: 'Multi-line (/* */)', openValue: '/*', closeValue: '*/' }
  ],
  'TypeScript': [
    { label: 'Single-line (//)', value: '//' },
    { label: 'Multi-line (/* */)', openValue: '/*', closeValue: '*/' },
    { label: 'JSDoc (/**)', openValue: '/**', closeValue: '*/' }
  ],
  'Go': [
    { label: 'Single-line (//)', value: '//' },
    { label: 'Multi-line (/* */)', openValue: '/*', closeValue: '*/' }
  ],
  'Rust': [
    { label: 'Single-line (//)', value: '//' },
    { label: 'Multi-line (/* */)', openValue: '/*', closeValue: '*/' }
  ],
  'C#': [
    { label: 'Single-line (//)', value: '//' },
    { label: 'Multi-line (/* */)', openValue: '/*', closeValue: '*/' },
    { label: 'XML doc (///)', value: '///' }
  ],
  'Swift': [
    { label: 'Single-line (//)', value: '//' },
    { label: 'Multi-line (/* */)', openValue: '/*', closeValue: '*/' }
  ],
  'Kotlin': [
    { label: 'Single-line (//)', value: '//' },
    { label: 'Multi-line (/* */)', openValue: '/*', closeValue: '*/' },
    { label: 'KDoc (/**)', openValue: '/**', closeValue: '*/' }
  ]
};

const SettingsSectionTitle = ({ title }) => (
  <h3 className="settings-page__section-title">{title}</h3>
);

function settingsLanguageIconName(language) {
  return normalizeLanguageIconKey(language) || 'code';
}

function SettingsOptionSelect({
  id,
  value,
  options,
  placeholder = 'Select',
  disabled = false,
  onChange,
  className = ''
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [value]);

  const selectedOption = options.find((option) => option.value === value);
  const displayLabel = selectedOption?.triggerLabel ?? selectedOption?.label ?? placeholder;

  return (
    <div
      ref={containerRef}
      className={`settings-page__language-select${disabled ? ' settings-page__language-select--disabled' : ''}${className ? ` ${className}` : ''}`}
    >
      <button
        type="button"
        id={id}
        className={`settings-page__language-select-trigger${open ? ' settings-page__language-select-trigger--open' : ''}`}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={selectedOption ? `${placeholder}: ${selectedOption.label}` : placeholder}
      >
        <span className="settings-page__language-select-value">
          <span className="settings-page__language-select-label">
            {displayLabel}
          </span>
        </span>
        <span className="settings-page__language-select-caret" aria-hidden="true" />
      </button>

      {open && !disabled && (
        <ul className="settings-page__language-select-menu" role="listbox" aria-labelledby={id}>
          {options.map((option) => (
            <li key={option.value} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={value === option.value}
                className={`settings-page__language-select-option${value === option.value ? ' settings-page__language-select-option--selected' : ''}`}
                onClick={() => onChange(option.value)}
              >
                <span>{option.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SettingsLanguageSelect({ id, value, languages, disabled, onChange }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [value]);

  const isDisabled = disabled || languages.length === 0;

  return (
    <div
      ref={containerRef}
      className={`settings-page__language-select${isDisabled ? ' settings-page__language-select--disabled' : ''}`}
    >
      <button
        type="button"
        id={id}
        className={`settings-page__language-select-trigger${open ? ' settings-page__language-select-trigger--open' : ''}`}
        onClick={() => !isDisabled && setOpen((prev) => !prev)}
        disabled={isDisabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={value ? `Language: ${value}` : 'Select language'}
      >
        <span className="settings-page__language-select-value">
          {value ? (
            <IconRenderer
              name={settingsLanguageIconName(value)}
              type="language"
              size="small"
              className="settings-page__language-select-icon"
            />
          ) : null}
          <span className="settings-page__language-select-label">
            {value || 'Select language'}
          </span>
        </span>
        <span className="settings-page__language-select-caret" aria-hidden="true" />
      </button>

      {open && !isDisabled && (
        <ul className="settings-page__language-select-menu" role="listbox" aria-labelledby={id}>
          {languages.map((language) => (
            <li key={language} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={value === language}
                className={`settings-page__language-select-option${value === language ? ' settings-page__language-select-option--selected' : ''}`}
                onClick={() => onChange(language)}
              >
                <IconRenderer
                  name={settingsLanguageIconName(language)}
                  type="language"
                  size="small"
                  className="settings-page__language-select-icon"
                />
                <span>{language}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const SettingsRow = ({ label, htmlFor, controlClassName = '', children }) => (
  <div className="settings-page__row">
    {htmlFor ? (
      <label className="settings-page__row-label" htmlFor={htmlFor}>{label}</label>
    ) : (
      <span className="settings-page__row-label">{label}</span>
    )}
    <div className={`settings-page__row-control${controlClassName ? ` ${controlClassName}` : ''}`}>
      {children}
    </div>
  </div>
);

const SettingsGroup = ({ title, children }) => (
  <section className="settings-page__group">
    {title ? <h4 className="settings-page__group-title">{title}</h4> : null}
    {children}
  </section>
);

const SettingsPlaceholderPanel = ({ title }) => (
  <>
    <SettingsSectionTitle title={title} />
    <p className="settings-page__coming-soon">Coming soon.</p>
  </>
);

const Settings = ({ userId }) => {
  const { availableLanguages, isLoading } = useUserLanguages(userId);
  const [activeSection, setActiveSection] = useState('code-processing');
  const [navQuery, setNavQuery] = useState('');
  const [patternType, setPatternType] = useState(PATTERN_TYPES.SIMPLE);
  const [newKeyword, setNewKeyword] = useState('');
  const [commentStyle, setCommentStyle] = useState('');
  const [commentText, setCommentText] = useState('');
  const [openPattern, setOpenPattern] = useState('');
  const [closePattern, setClosePattern] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [languagePatterns, setLanguagePatterns] = useState({});

  // Set the first language as selected if none is selected, using useEffect
  useEffect(() => {
    if (!selectedLanguage && availableLanguages.length > 0) {
      setSelectedLanguage(availableLanguages[0]);
    }
  }, [selectedLanguage, availableLanguages]);

  // Auto-select default comment style when language or pattern type changes
  useEffect(() => {
    if (patternType !== PATTERN_TYPES.COMMENT || !selectedLanguage) return;

    const inlineStyles = (LANGUAGE_COMMENT_STYLES[selectedLanguage] || []).filter((style) => style.value);
    if (inlineStyles.length === 0) return;

    const isCurrentValid = inlineStyles.some((style) => style.value === commentStyle);
    if (!commentStyle || !isCurrentValid) {
      setCommentStyle(inlineStyles[0].value);
    }
  }, [patternType, selectedLanguage, commentStyle]);

  // Fetch ignore patterns for all languages
  useEffect(() => {
    const fetchIgnorePatterns = async () => {
      try {
        const userSettingsRef = doc(db, `users/${userId}/settings/codeProcessing`);
        const settingsDoc = await getDoc(userSettingsRef);
        let updatedPatterns = {};
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          if (data.languagePatterns) {
            updatedPatterns = { ...data.languagePatterns };
          } else if (data.languageKeywords) {
            Object.entries(data.languageKeywords).forEach(([lang, keywords]) => {
              updatedPatterns[lang] = keywords.map(keyword => ({
                type: PATTERN_TYPES.SIMPLE,
                pattern: keyword,
                caseSensitive: false,
                enabled: true
              }));
            });
          } else if (data.ignoreKeywords) {
            availableLanguages.forEach(lang => {
              const combinedKeywords = [...new Set([...DEFAULT_IGNORE_KEYWORDS, ...data.ignoreKeywords])];
              updatedPatterns[lang] = combinedKeywords.map(keyword => ({
                type: PATTERN_TYPES.SIMPLE,
                pattern: keyword,
                caseSensitive: false,
                enabled: true
              }));
            });
          }
        }
        // Wave 1: languages without saved data get full built-in patterns for that language
        availableLanguages.forEach(lang => {
          if (!updatedPatterns[lang]) {
            updatedPatterns[lang] = getBuiltinIgnorePatternsForLanguage(lang);
          }
        });
        setLanguagePatterns(updatedPatterns);
      } catch (error) {
        console.error('Error fetching ignore keywords:', error);
      }
    };
    if (availableLanguages.length > 0) {
      fetchIgnorePatterns();
    }
  }, [userId, availableLanguages]);

  // Handler for adding a simple keyword pattern
  const handleAddKeyword = async () => {
    if (newKeyword.trim() === '' || !selectedLanguage) return;
    const newPattern = {
      type: PATTERN_TYPES.SIMPLE,
      pattern: newKeyword.trim(),
      caseSensitive: caseSensitive,
      enabled: true
    };
    const languagePatternList = languagePatterns[selectedLanguage] || [];
    if (languagePatternList.some(p => p.type === PATTERN_TYPES.SIMPLE && p.pattern === newPattern.pattern)) {
      setSaveStatus(`Pattern already exists for ${selectedLanguage}`);
      setTimeout(() => setSaveStatus(''), 3000);
      return;
    }
    const updatedPatterns = {
      ...languagePatterns,
      [selectedLanguage]: [...languagePatternList, newPattern]
    };
    setLanguagePatterns(updatedPatterns);
    setNewKeyword('');
    try {
      setSaveStatus(`Adding pattern to ${selectedLanguage}...`);
      const userSettingsRef = doc(db, `users/${userId}/settings/codeProcessing`);
      await setDoc(userSettingsRef, { languagePatterns: updatedPatterns }, { merge: true });
      setSaveStatus(`Pattern added to ${selectedLanguage}`);
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error saving pattern:', error);
      setSaveStatus('Error saving pattern');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  // Handler for adding a comment pattern
  const handleAddCommentPattern = async () => {
    if (!commentStyle || !commentText.trim() || !selectedLanguage) {
      setSaveStatus('Please select a comment style and enter comment text');
      setTimeout(() => setSaveStatus(''), 3000);
      return;
    }
    const newPattern = {
      type: PATTERN_TYPES.COMMENT,
      pattern: `${commentStyle}${commentText.trim()}`,
      commentStyle: commentStyle,
      commentText: commentText.trim(),
      caseSensitive: caseSensitive,
      enabled: true
    };
    const languagePatternList = languagePatterns[selectedLanguage] || [];
    if (languagePatternList.some(p => p.type === PATTERN_TYPES.COMMENT && p.pattern === newPattern.pattern)) {
      setSaveStatus(`Pattern already exists for ${selectedLanguage}`);
      setTimeout(() => setSaveStatus(''), 3000);
      return;
    }
    const updatedPatterns = {
      ...languagePatterns,
      [selectedLanguage]: [...languagePatternList, newPattern]
    };
    setLanguagePatterns(updatedPatterns);
    setCommentText('');
    try {
      setSaveStatus(`Adding pattern to ${selectedLanguage}...`);
      const userSettingsRef = doc(db, `users/${userId}/settings/codeProcessing`);
      await setDoc(userSettingsRef, { languagePatterns: updatedPatterns }, { merge: true });
      setSaveStatus(`Pattern added to ${selectedLanguage}`);
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error saving pattern:', error);
      setSaveStatus('Error saving pattern');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  // Handler for adding a multiline pattern
  const handleAddMultilinePattern = async () => {
    if (!openPattern.trim() || !closePattern.trim() || !selectedLanguage) {
      setSaveStatus('Please enter both opening and closing patterns');
      setTimeout(() => setSaveStatus(''), 3000);
      return;
    }
    const newPattern = {
      type: PATTERN_TYPES.MULTILINE,
      openPattern: openPattern.trim(),
      closePattern: closePattern.trim(),
      caseSensitive: caseSensitive,
      enabled: true
    };
    const languagePatternList = languagePatterns[selectedLanguage] || [];
    if (languagePatternList.some(p =>
      p.type === PATTERN_TYPES.MULTILINE &&
      p.openPattern === newPattern.openPattern &&
      p.closePattern === newPattern.closePattern
    )) {
      setSaveStatus(`Pattern already exists for ${selectedLanguage}`);
      setTimeout(() => setSaveStatus(''), 3000);
      return;
    }
    const updatedPatterns = {
      ...languagePatterns,
      [selectedLanguage]: [...languagePatternList, newPattern]
    };
    setLanguagePatterns(updatedPatterns);
    setOpenPattern('');
    setClosePattern('');
    try {
      setSaveStatus(`Adding pattern to ${selectedLanguage}...`);
      const userSettingsRef = doc(db, `users/${userId}/settings/codeProcessing`);
      await setDoc(userSettingsRef, { languagePatterns: updatedPatterns }, { merge: true });
      setSaveStatus(`Pattern added to ${selectedLanguage}`);
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error saving pattern:', error);
      setSaveStatus('Error saving pattern');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  // Handler for removing a pattern
  const handleRemovePattern = async (language, index) => {
    const updatedPatterns = {
      ...languagePatterns,
      [language]: (languagePatterns[language] || []).filter((_, i) => i !== index)
    };
    setLanguagePatterns(updatedPatterns);
    try {
      setSaveStatus(`Removing pattern from ${language}...`);
      const userSettingsRef = doc(db, `users/${userId}/settings/codeProcessing`);
      await setDoc(userSettingsRef, { languagePatterns: updatedPatterns }, { merge: true });
      setSaveStatus(`Pattern removed from ${language}`);
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error removing pattern:', error);
      setSaveStatus('Error removing pattern');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaveStatus('Saving...');
      const userSettingsRef = doc(db, `users/${userId}/settings/codeProcessing`);
      await setDoc(userSettingsRef, { languagePatterns }, { merge: true });
      setSaveStatus('Settings saved successfully');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveStatus('Error saving settings');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const filteredNav = SETTINGS_NAV.filter((item) =>
    item.label.toLowerCase().includes(navQuery.trim().toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="settings-page">
        <p className="settings-page__loading">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <h2 className="settings-page__title">Settings</h2>

      <div className="settings-page__shell">
        <nav className="settings-page__sidebar" aria-label="Settings sections">
          <div className="settings-page__sidebar-search">
            <span className="settings-page__sidebar-search-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-3-3" />
              </svg>
            </span>
            <input
              type="search"
              value={navQuery}
              onChange={(e) => setNavQuery(e.target.value)}
              placeholder="Search"
              className="settings-page__sidebar-search-input"
              aria-label="Search settings"
            />
          </div>
          <div className="settings-page__nav-list" role="list">
            {filteredNav.map((item) => (
              <button
                key={item.id}
                type="button"
                role="listitem"
                className={`settings-page__nav-item${activeSection === item.id ? ' settings-page__nav-item--active' : ''}`}
                onClick={() => setActiveSection(item.id)}
                aria-current={activeSection === item.id ? 'page' : undefined}
              >
                <span className="settings-page__nav-icon">
                  <SettingsNavIcon sectionId={item.id} />
                </span>
                <span className="settings-page__nav-label">{item.label}</span>
              </button>
            ))}
            {filteredNav.length === 0 && (
              <p className="settings-page__nav-empty">No matching sections</p>
            )}
          </div>
        </nav>

        <div className="settings-page__content">
          <div className="settings-page__section">
            <div className="settings-page__panel" hidden={activeSection !== 'account'}>
              <SettingsSectionTitle title="Account" />
              <AccountSettings userId={userId} />
            </div>

            <div className="settings-page__panel" hidden={activeSection !== 'study'}>
              <SettingsPlaceholderPanel title={SETTINGS_PLACEHOLDER_TITLES.study} />
            </div>

            <div className="settings-page__panel" hidden={activeSection !== 'notifications'}>
              <SettingsPlaceholderPanel title={SETTINGS_PLACEHOLDER_TITLES.notifications} />
            </div>

            <div className="settings-page__panel" hidden={activeSection !== 'code-processing'}>
                <SettingsSectionTitle title="Code Processing" />
                <p className="settings-page__description">
                  Lines matching these patterns are skipped during study mode. Defaults are set per language. Add custom ones if needed.
                </p>

                <SettingsGroup>
                  <SettingsRow label="Language" htmlFor="language-select">
                    <SettingsLanguageSelect
                      id="language-select"
                      value={selectedLanguage}
                      languages={availableLanguages}
                      disabled={isLoading}
                      onChange={setSelectedLanguage}
                    />
                  </SettingsRow>

                  {selectedLanguage && (
                    <>
                      <SettingsRow label="Pattern type">
                        <div className="settings-page__pattern-type-options">
                          <label
                            className={`settings-page__pattern-option${patternType === PATTERN_TYPES.SIMPLE ? ' settings-page__pattern-option--selected' : ''}`}
                          >
                            <input
                              type="radio"
                              name="patternType"
                              value={PATTERN_TYPES.SIMPLE}
                              checked={patternType === PATTERN_TYPES.SIMPLE}
                              onChange={() => setPatternType(PATTERN_TYPES.SIMPLE)}
                            />
                            <div className="settings-page__pattern-option-content">
                              <span className="settings-page__pattern-option-icon" aria-hidden="true">
                                <PatternKeywordIcon />
                              </span>
                              <span className="settings-page__pattern-option-title">Simple</span>
                            </div>
                          </label>

                          <label
                            className={`settings-page__pattern-option${patternType === PATTERN_TYPES.COMMENT ? ' settings-page__pattern-option--selected' : ''}`}
                          >
                            <input
                              type="radio"
                              name="patternType"
                              value={PATTERN_TYPES.COMMENT}
                              checked={patternType === PATTERN_TYPES.COMMENT}
                              onChange={() => setPatternType(PATTERN_TYPES.COMMENT)}
                            />
                            <div className="settings-page__pattern-option-content">
                              <span className="settings-page__pattern-option-icon" aria-hidden="true">
                                <PatternCommentIcon />
                              </span>
                              <span className="settings-page__pattern-option-title">Comment</span>
                            </div>
                          </label>

                          <label
                            className={`settings-page__pattern-option${patternType === PATTERN_TYPES.MULTILINE ? ' settings-page__pattern-option--selected' : ''}`}
                          >
                            <input
                              type="radio"
                              name="patternType"
                              value={PATTERN_TYPES.MULTILINE}
                              checked={patternType === PATTERN_TYPES.MULTILINE}
                              onChange={() => setPatternType(PATTERN_TYPES.MULTILINE)}
                            />
                            <div className="settings-page__pattern-option-content">
                              <span className="settings-page__pattern-option-icon" aria-hidden="true">
                                <PatternMultilineIcon />
                              </span>
                              <span className="settings-page__pattern-option-title">Multi-line</span>
                            </div>
                          </label>
                        </div>
                      </SettingsRow>

                      <SettingsRow label="Case sensitive" htmlFor="case-sensitive" controlClassName="settings-page__row-control--toggle">
                        <input
                          type="checkbox"
                          id="case-sensitive"
                          className="settings-page__checkbox"
                          checked={caseSensitive}
                          onChange={(e) => setCaseSensitive(e.target.checked)}
                        />
                      </SettingsRow>

                      <SettingsRow label="Add pattern">
                        {patternType === PATTERN_TYPES.SIMPLE && (
                          <div className="settings-page__input-row settings-page__input-row--pattern">
                            <input
                              type="text"
                              value={newKeyword}
                              onChange={(e) => setNewKeyword(e.target.value)}
                              placeholder="e.g., TODO, FIXME"
                              className="settings-page__input settings-page__input--row"
                              onKeyPress={(e) => e.key === 'Enter' && handleAddKeyword()}
                            />
                            <button
                              type="button"
                              onClick={handleAddKeyword}
                              className="settings-page__btn settings-page__btn--add"
                              aria-label="Add pattern"
                            >
                              <AddIcon className="settings-page__add-icon" aria-hidden />
                            </button>
                          </div>
                        )}

                        {patternType === PATTERN_TYPES.COMMENT && (
                          <div className="settings-page__input-row settings-page__input-row--pattern settings-page__input-row--comment">
                            <div className="settings-page__comment-inputs">
                              <SettingsOptionSelect
                                id="comment-style-select"
                                value={commentStyle}
                                options={(LANGUAGE_COMMENT_STYLES[selectedLanguage] || [])
                                  .filter((style) => style.value)
                                  .map((style) => ({
                                    value: style.value,
                                    label: style.label,
                                    triggerLabel: style.value
                                  }))}
                                placeholder="Comment style"
                                onChange={setCommentStyle}
                                className="settings-page__comment-style-select"
                              />
                              <input
                                type="text"
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                                placeholder="e.g., DEBUG, NOTE"
                                className="settings-page__input settings-page__input--row"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={handleAddCommentPattern}
                              className="settings-page__btn settings-page__btn--add"
                              aria-label="Add pattern"
                            >
                              <AddIcon className="settings-page__add-icon" aria-hidden />
                            </button>
                          </div>
                        )}

                        {patternType === PATTERN_TYPES.MULTILINE && (
                          <div className="settings-page__input-row settings-page__input-row--pattern">
                            <div className="settings-page__multiline-inputs">
                              <input
                                id="open-pattern"
                                type="text"
                                value={openPattern}
                                onChange={(e) => setOpenPattern(e.target.value)}
                                placeholder={`Opening, e.g. ${selectedLanguage === 'JavaScript' ? '/* REVIEW' : selectedLanguage === 'Python' ? "'''IGNORE" : '/* BLOCK'}`}
                                aria-label="Opening pattern"
                                className="settings-page__input settings-page__input--row"
                              />
                              <input
                                id="close-pattern"
                                type="text"
                                value={closePattern}
                                onChange={(e) => setClosePattern(e.target.value)}
                                placeholder={`Closing, e.g. ${selectedLanguage === 'JavaScript' ? '*/' : selectedLanguage === 'Python' ? "'''" : '*/'}`}
                                aria-label="Closing pattern"
                                className="settings-page__input settings-page__input--row"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={handleAddMultilinePattern}
                              className="settings-page__btn settings-page__btn--add"
                              aria-label="Add pattern"
                            >
                              <AddIcon className="settings-page__add-icon" aria-hidden />
                            </button>
                          </div>
                        )}
                      </SettingsRow>
                    </>
                  )}
                </SettingsGroup>

                {selectedLanguage && (
                  <>
                    <SettingsGroup title="Suggested patterns">
                      <div className="settings-page__pattern-tags">
                        {getBuiltinSuggestionStringsForLanguage(selectedLanguage).map((text, i) => (
                          <span key={`${text}-${i}`} className="settings-page__pattern-tag">{text}</span>
                        ))}
                      </div>
                    </SettingsGroup>

                    <SettingsGroup title={`Active patterns (${selectedLanguage})`}>
                      {languagePatterns[selectedLanguage] && languagePatterns[selectedLanguage].length > 0 ? (
                        <ul className="settings-page__patterns-items">
                          {languagePatterns[selectedLanguage].map((pattern, index) => (
                            <li key={index} className={`settings-page__pattern-item settings-page__pattern-item--${pattern.type}`}>
                              <div className="settings-page__pattern-item-info">
                                <span className="settings-page__pattern-item-dot" aria-hidden="true" />
                                <span className="settings-page__pattern-item-text">
                                  {pattern.type === PATTERN_TYPES.SIMPLE && pattern.pattern}
                                  {pattern.type === PATTERN_TYPES.COMMENT && pattern.pattern}
                                  {pattern.type === PATTERN_TYPES.MULTILINE && (
                                    <>
                                      {pattern.openPattern} … {pattern.closePattern}
                                    </>
                                  )}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemovePattern(selectedLanguage, index)}
                                className="settings-page__btn settings-page__btn--danger"
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="settings-page__empty-state">No patterns added for {selectedLanguage}</p>
                      )}
                    </SettingsGroup>
                  </>
                )}

                <div className="settings-page__actions">
                  <button type="button" onClick={handleSaveSettings} className="settings-page__btn settings-page__btn--save">
                    Save settings
                  </button>
                  {saveStatus && <p className="settings-page__save-status">{saveStatus}</p>}
                </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
